import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Combobox, Transition } from '@headlessui/react';
import { XMarkIcon } from '@heroicons/react/24/solid';

function CategoryInput(props) {
  // Handle both camelCase and kebab-case props from remount
  const initialCategories = props.initialCategories || props['initial-categories'] || '[]';
  const allCategories = props.allCategories || props['all-categories'] || '[]';
  
  // Parse the JSON strings passed from Rails
  const parsedInitialCategories = typeof initialCategories === 'string' ? JSON.parse(initialCategories) : initialCategories;
  const parsedAllCategories = typeof allCategories === 'string' ? JSON.parse(allCategories) : allCategories;
  
  
  const [categories, setCategories] = useState(parsedInitialCategories);
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const inputRef = useRef(null);

  const getCategoryColor = useCallback((category) => {
    const categoryKey = category.toLowerCase().replace(/[\s&]+/g, '_');
    
    // Direct mappings
    const directMappings = {
      'cinema': 'bg-film',
      'classes_&_workshops': 'bg-workshops'
    };
    
    if (directMappings[categoryKey]) {
      return directMappings[categoryKey];
    }
    
    // Base colors
    const baseColors = ['christmas', 'comedy', 'dance', 'family', 'film', 'music', 'online', 'relaxed', 'spoken', 'jubilee', 'schools', 'theatre', 'visual', 'workshops', 'youth'];
    
    if (baseColors.includes(categoryKey)) {
      return `bg-${categoryKey}`;
    }
    
    // Category mappings
    const mappings = {
      'ballet': 'bg-dance',
      'bluegrass': 'bg-music',
      'blues': 'bg-music',
      'classical': 'bg-music',
      'folk': 'bg-music',
      'jazz': 'bg-music',
      'rock': 'bg-music',
      'swing': 'bg-music',
      'world': 'bg-music',
      'non_classical_music': 'bg-music',
      'festival': 'bg-music',
      'bring_baby': 'bg-family',
      'parent_and_baby': 'bg-family',
      'parent_and_baby_screening': 'bg-family',
      'parent_baby_screening': 'bg-family',
      'explore_day': 'bg-family',
      'half_term': 'bg-family',
      'jump_in': 'bg-family',
      'kite_festival': 'bg-family',
      'quest': 'bg-family',
      'school_holiday': 'bg-family',
      'cabaret': 'bg-comedy',
      'light_entertainment': 'bg-comedy',
      'children_young_people': 'bg-youth',
      'young_people': 'bg-youth',
      'classes_workshops': 'bg-workshops',
      'workshops_and_classes': 'bg-workshops',
      'workshops_classes': 'bg-workshops',
      'literature': 'bg-workshops',
      'community': 'bg-christmas',
      'community_hire': 'bg-christmas',
      'food_drink': 'bg-christmas',
      'drama': 'bg-theatre',
      'opera': 'bg-theatre',
      'event_cinema': 'bg-film',
      'live_cinema': 'bg-film',
      'gallery': 'bg-visual',
      'visual_art': 'bg-visual',
      'visual_arts': 'bg-visual',
      'markets': 'bg-relaxed',
      'talk': 'bg-spoken'
    };
    
    return mappings[categoryKey] || 'bg-gray-500';
  }, []);

  const filteredSuggestions = query === ''
    ? []
    : parsedAllCategories
        .filter(cat => !categories.includes(cat))
        .filter(cat => cat.toLowerCase().includes(query.toLowerCase()))
        .slice(0, 10); // Show more suggestions

  const addCategory = useCallback((category) => {
    if (category && !categories.includes(category)) {
      setCategories(prev => [...prev, category]);
      setQuery('');
    }
  }, [categories]);

  const removeCategory = useCallback((index) => {
    setCategories(prev => prev.filter((_, i) => i !== index));
  }, []);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = query.trim();
      if (trimmed) {
        addCategory(trimmed);
      }
    }
  };

  return (
    <div className="space-y-3">
      {/* Category tags display */}
      <div className="flex flex-wrap gap-2">
        {categories.map((category, index) => (
          <div
            key={category}
            className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium text-white ${getCategoryColor(category)}`}
          >
            <span>{category}</span>
            <button
              type="button"
              onClick={() => removeCategory(index)}
              className="ml-1 hover:text-gray-200"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      
      {/* Hidden field with comma-separated categories */}
      <input type="hidden" name="event[category_list]" value={categories.join(',')} />

      {/* Category input with autocomplete */}
      <div className="relative">
        <Combobox value={query} onChange={addCategory}>
          <div className="relative">
            <Combobox.Input
              ref={inputRef}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={() => setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
              placeholder="Type to add a category..."
              className="w-full px-3 py-2 rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
            />
            
            <Transition
              show={showSuggestions && filteredSuggestions.length > 0}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Combobox.Options
                static
                className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 rounded-md shadow-lg border border-gray-200 dark:border-gray-700"
              >
                {filteredSuggestions.map((suggestion) => {
                  // Highlight matching text
                  const index = suggestion.toLowerCase().indexOf(query.toLowerCase());
                  const beforeMatch = suggestion.slice(0, index);
                  const match = suggestion.slice(index, index + query.length);
                  const afterMatch = suggestion.slice(index + query.length);
                  
                  return (
                    <Combobox.Option
                      key={suggestion}
                      value={suggestion}
                      className={({ active }) =>
                        `cursor-pointer px-3 py-2 ${
                          active ? 'bg-gray-100 dark:bg-gray-700' : ''
                        }`
                      }
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-gray-700 dark:text-gray-300">
                          {beforeMatch}
                          <span className="font-semibold text-blue-600 dark:text-blue-400">{match}</span>
                          {afterMatch}
                        </span>
                        <span
                          className={`px-2 py-1 text-xs font-medium text-white rounded-full ${getCategoryColor(suggestion)}`}
                        >
                          {suggestion}
                        </span>
                      </div>
                    </Combobox.Option>
                  );
                })}
              </Combobox.Options>
            </Transition>
          </div>
        </Combobox>
      </div>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        Press Enter to add a category. You can add new categories or select from existing ones.
      </p>
    </div>
  );
}

export default CategoryInput;